<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import DToastStack from '../../../components/notifications/DToastStack.vue'
import { useNotificationsStore } from '@/store/notifications'
import {
  DCard,
  DBtn,
  DLoading,
  InfoCircleIcon,
  Mail01Icon,
  CheckCircleIcon,
  AlertCircleIcon
} from 'front-library'
import {
  accessShare,
  decideShare,
  requestShareAccess,
  ShareAccessRequest,
  ShareRequestError
} from '../../../core/services/document-shares'
/* El backend envia ISO con Z, asi que dayjs ya lo pasa a hora local sin plugins */
import dayjs from 'dayjs'

const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* El correo de notificacion trae ?data=, y el del enlace magico trae ?access= */
const shareData = route.query.data as string | undefined
const accessToken = route.query.access as string | undefined

/* Datos componente DLoading */
const loading = ref(false)
const messageLoading = ref('')

/* URL prefirmada del desprendible, una vez canjeado el token */
const urlFile = ref('')

/* Vigencia del enlace mientras el backend no diga otra cosa */
const DEFAULT_LINK_MINUTES = 5

/* Espera de respaldo cuando el backend no informa cuanto falta */
const RESEND_COOLDOWN_SECONDS = 30

/* Se envio el correo con el enlace magico */
const linkSent = ref(false)
const linkMinutes = ref(DEFAULT_LINK_MINUTES)

/* El token de la URL ya vencio: se ofrece reenviarlo con ese mismo token */
const expiredLink = ref(false)

/* No se pudo enviar el correo: se ofrece reintentar tras el contador */
const sendFailed = ref(false)

/* Segundos restantes antes de poder pedir otro enlace */
const cooldown = ref(0)
let cooldownTimer: ReturnType<typeof setInterval> | undefined

const stopCooldown = () => {
  if (cooldownTimer) {
    clearInterval(cooldownTimer)
    cooldownTimer = undefined
  }
}

const startCooldown = (seconds: number) => {
  stopCooldown()
  cooldown.value = Math.max(0, Math.trunc(seconds))
  if (cooldown.value === 0) return
  cooldownTimer = setInterval(() => {
    cooldown.value -= 1
    if (cooldown.value <= 0) {
      cooldown.value = 0
      stopCooldown()
    }
  }, 1000)
}

onUnmounted(stopCooldown)

const canResend = computed(() => cooldown.value === 0 && !loading.value)

/* Aprobacion del empleado: el backend la devuelve en cada acceso, para que
 * sobreviva a un refresco de la pagina */
const approvalEnabled = ref(false)
const approved = ref(false)
const dateApproval = ref('')

/* Enlace roto: llegaron sin data ni access */
const invalidLink = computed(() => !shareData && !accessToken)

/* Traduce el codigo de error del backend a un mensaje para el empleado */
const messageFor = (error: unknown): string => {
  const code = error instanceof ShareRequestError ? error.code : 'UNKNOWN'
  return t(`shareAccess.errors.${code}`)
}

const showError = (error: unknown) => {
  notifications.addNotification({
    title: t('shareAccess.errors.title'),
    text: messageFor(error),
    type: 'error',
    time: 10000
  })
}

/* El token vencido sirve para pedir uno nuevo cuando ya no queda el ?data= */
const accessRequest = computed<ShareAccessRequest | null>(() => {
  if (shareData) return { data: shareData }
  if (accessToken) return { access: accessToken }
  return null
})

/* Paso 1: pedir que envien el enlace magico al correo del empleado */
const askForLink = async () => {
  const link = accessRequest.value
  if (!link) return

  loading.value = true
  messageLoading.value = t('shareAccess.loading.requesting')
  try {
    const result = await requestShareAccess(link)
    linkSent.value = true
    expiredLink.value = false
    sendFailed.value = false
    linkMinutes.value = Math.max(1, Math.round(result.expiresInSeconds / 60))
    startCooldown(result.retryAfterSeconds)
    notifications.addNotification({
      title: t('shareAccess.notification.sentTitle'),
      text: t('shareAccess.notification.sentText'),
      type: 'success',
      time: 10000
    })
  } catch (error: unknown) {
    const alreadySent =
      error instanceof ShareRequestError &&
      error.code === 'ShareAccessRequestTooSoon'

    /* Ya habia uno en camino: el correo esta en la bandeja, no es un fallo */
    linkSent.value = alreadySent
    sendFailed.value = !alreadySent
    expiredLink.value = false
    startCooldown(
      alreadySent && error instanceof ShareRequestError
        ? error.retryAfterSeconds ?? RESEND_COOLDOWN_SECONDS
        : RESEND_COOLDOWN_SECONDS
    )
    showError(error)
  } finally {
    loading.value = false
  }
}

/* Paso 2: canjear el token del enlace magico por el desprendible */
const openReceipt = async () => {
  if (!accessToken) return

  loading.value = true
  messageLoading.value = t('shareAccess.loading.opening')
  try {
    const result = await accessShare(accessToken)
    urlFile.value = result.downloadUrl
    approvalEnabled.value = result.approvalEnabled
    approved.value = result.approved
    dateApproval.value = result.approvedAt ?? ''
  } catch (error: unknown) {
    /* El vencimiento se explica en la tarjeta, sin toast que tape el reenvio */
    if (
      error instanceof ShareRequestError &&
      error.code === 'ACCESS_TOKEN_EXPIRED'
    ) {
      expiredLink.value = true
      return
    }
    showError(error)
  } finally {
    loading.value = false
  }
}

/* Paso 3 (opcional): aprobar el desprendible con el mismo token */
const approve = async () => {
  if (!accessToken) return

  loading.value = true
  messageLoading.value = t('shareAccess.loading.approving')
  try {
    await decideShare(accessToken, 'APPROVE')
    approved.value = true
    dateApproval.value = new Date().toISOString()
    notifications.addNotification({
      title: t('shareAccess.notification.approvedTitle'),
      text: t('shareAccess.notification.approvedText'),
      type: 'success',
      time: 10000
    })
  } catch (error: unknown) {
    showError(error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  /* Con el token se abre el desprendible; con el enlace del correo el envio
   * arranca solo, sin que el empleado tenga que pedirlo */
  if (accessToken) {
    void openReceipt()
    return
  }
  if (shareData) void askForLink()
})
</script>
<template>
  <DToastStack class="left-0" />
  <div class="h-screen w-screen p-4 bg-gray-50">
    <DLoading v-if="loading" :message="messageLoading" />

    <div
      v-if="!urlFile"
      class="flex flex-col gap-5 justify-center items-center h-full w-full">
      <div>
        <img
          src="/src/assets/logo.png"
          class="h-20 static logo-aps"
          alt="Logo APS" />
      </div>

      <!-- DCard trae sm:w-full: sin este contenedor ocuparia todo el ancho -->
      <div class="w-full max-w-md">
        <DCard :footer="true" :header="true">
          <!-- Header -->
          <template #header>
            <h1
              class="tracking-wide font-medium text-center"
              v-html="t('shareAccess.title')" />
          </template>

          <!-- Body -->
          <template #default>
            <!-- Enlace sin data ni access -->
            <div
              v-if="invalidLink"
              class="w-full p-5 bg-red-50 rounded-lg flex text-red-600 gap-3 items-center">
              <AlertCircleIcon class="h-8 shrink-0" />
              <p>{{ t('shareAccess.invalidLink') }}</p>
            </div>

            <!-- Ya se envio el enlace magico -->
            <div
              v-else-if="linkSent"
              class="w-full p-5 bg-green-50 rounded-lg flex text-green-700 gap-3 items-center">
              <CheckCircleIcon class="h-8 shrink-0" />
              <p>{{ t('shareAccess.linkSent', { minutes: linkMinutes }) }}</p>
            </div>

            <!-- El enlace de la URL vencio -->
            <div
              v-else-if="expiredLink"
              class="w-full p-5 bg-red-50 rounded-lg flex text-red-600 gap-3 items-center">
              <AlertCircleIcon class="h-8 shrink-0" />
              <p>{{ t('shareAccess.expiredLink') }}</p>
            </div>

            <!-- No se pudo enviar el correo -->
            <div
              v-else-if="sendFailed"
              class="w-full p-5 bg-red-50 rounded-lg flex text-red-600 gap-3 items-center">
              <AlertCircleIcon class="h-8 shrink-0" />
              <p>{{ t('shareAccess.sendFailed') }}</p>
            </div>

            <!-- El token venia en la URL pero fallo al canjearlo -->
            <div
              v-else-if="accessToken"
              class="w-full p-5 bg-yellow-50 rounded-lg flex text-yellow-600 gap-3 items-center">
              <InfoCircleIcon class="h-8 shrink-0" />
              <p>{{ t('shareAccess.accessFailed') }}</p>
            </div>

            <!-- El envio arranca al montar: solo se ve mientras responde -->
            <div
              v-else
              class="w-full p-5 bg-yellow-50 rounded-lg flex text-yellow-600 gap-3 items-center">
              <InfoCircleIcon class="h-8 shrink-0" />
              <p>
                {{
                  t('shareAccess.sending', { minutes: DEFAULT_LINK_MINUTES })
                }}
              </p>
            </div>
          </template>

          <!-- Footer -->
          <template #footer>
            <div class="flex flex-col gap-2 items-center justify-center">
              <!-- Reenvio: tras enviar, tras fallar o cuando el token vencio -->
              <DBtn
                v-if="linkSent || expiredLink || sendFailed"
                color="blue"
                class="w-full flex items-center justify-center gap-2"
                :disabled="!canResend"
                @click="askForLink">
                <Mail01Icon class="w-5 h-5" />
                {{
                  canResend
                    ? t('shareAccess.button.resend')
                    : t('shareAccess.button.resendIn', { seconds: cooldown })
                }}
              </DBtn>

              <DBtn
                v-else-if="accessToken"
                color="blue"
                class="w-full"
                :disabled="loading"
                @click="openReceipt">
                {{ t('shareAccess.button.retry') }}
              </DBtn>
            </div>
          </template>
        </DCard>
      </div>
    </div>

    <iframe
      v-if="urlFile"
      :src="urlFile"
      :title="t('shareAccess.title')"
      class="h-full w-full" />

    <DBtn
      v-if="urlFile && approvalEnabled && !approved"
      color="excel"
      class="fixed bottom-[5%] right-[5%] px-5 py-2 font-semibold"
      :disabled="loading"
      @click.prevent="approve"
      v-html="t('shareAccess.button.approve')" />

    <span
      v-if="urlFile && approvalEnabled && approved"
      class="fixed bottom-[5%] right-[5%] inline-flex items-center rounded-md bg-green-50 px-5 py-2 font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
      {{
        t('shareAccess.approvedAt') +
        ' ' +
        dayjs(dateApproval).format('DD/MM/YYYY HH:mm:ss')
      }}
    </span>
  </div>
</template>
